import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Col,
  ConfigProvider,
  Divider,
  Flex,
  Radio,
  Row,
  Segmented,
  Space,
  Switch,
  Tabs,
  Typography,
  theme as antdTheme,
  Skeleton,
  Button,
  Modal,
  Table,
  Tooltip,
} from "antd";
import type { SegmentedLabeledOption } from "antd/es/segmented";
import type { TabsProps } from "antd";
import {
  DatabaseOutlined,
  FileExcelOutlined,
  FundOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
// import customParseFormat from "dayjs/plugin/customParseFormat";
import { refreshByHospitalId } from "../../apis/dashboardService";
import {
  downloadExcelOperationalDashboard,
  fetchOperationalDashboard,
  fetchPatientsOPDashboard,
} from "../../apis/dashboardService";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { getAllSchemes } from "../../models/patientSlice";
import "./index.css";
import { DatePicker } from "antd";
import { Select } from "antd";
import { errorMessage, successMessage } from "../../utils/notificationUtils";
import { getSchemeDisplayName } from "../../utils/schemeDisplay";
import {
  resetHospitalToSchemeCodesMap,
  updateSelectedHospital,
} from "../../models/hospitalSlice";
import { resetAddIPDBillState } from "../../models/ipdBillsSlice";
import { errorMessageModal } from "../../utils/modalMessage";

const { Title, Text, Link } = Typography;
const { RangePicker } = DatePicker;

type TimeRange = "today" | "weekly" | "monthly" | "quarterly";
type PatientType = "IPD" | "OPD";
type MetricMode = "amount" | "count";

interface Filters {
  submissionPart: "pre" | "post";
  timeRange: TimeRange;
  timeRangePost: TimeRange;
  scheme: any;
  schemePost: any;
  patientType: PatientType;
  patientTypePost: PatientType;
  metricMode: MetricMode;
  metricModePost: MetricMode;
  startDate: any;
  startDatePost: string | null;
  endDate: any;
  endDatePost: string | null;
}

interface StageMetric {
  label: string;
  value: number;
}

interface StageBlock {
  key: string;
  heading: string;
  ribbon?: {
    text: string;
    metrics?: StageMetric[];
    color?: string;
  };
  tiles?: Array<{
    title: string;
    metrics: StageMetric[];
    color: string;
  }>;
}

// Professional accent palette and helpers
const hexToRgb = (hex?: string): { r: number; g: number; b: number } | null => {
  if (!hex) return null;
  const normalized = hex.replace("#", "");
  if (normalized.length !== 6) return null;
  const r = parseInt(normalized.substring(0, 2), 16);
  const g = parseInt(normalized.substring(2, 4), 16);
  const b = parseInt(normalized.substring(4, 6), 16);
  return { r, g, b };
};

const getAccentFor = (title?: string, _fallback?: string): string => {
  const key = (title || "").toLowerCase();
  const palette: Record<string, string> = {
    // Core palette (overrides any backend-provided colors)
    "portal registration": "#2563eb", // blue
    "visit created": "#0ea5e9", // sky
    preauthorisation: "#16a34a", // green
    submitted: "#4f46e5", // indigo
    "internal query": "#dc2626", // red
    "qc query": "#d97706", // amber
    discharged: "#334155", // slate
  };
  // Always use professional palette; ignore backend fallback color
  return palette[key] || "#3b82f6";
};

const makeSoftBg = (accentHex?: string, alpha: number = 0.05): string => {
  const rgb = hexToRgb(accentHex);
  if (!rgb) return "rgba(0,0,0,0.03)";
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

const transform = (key: string, section: { title: string; cards: any[] }) => {
  if (!section) {
    return null;
  }
  const cards = section.cards || [];
  let ribbonCard: any;
  let tileCards;

  if (cards.length > 4) {
    // ribbonCard = cards[4];
    // tileCards = [...cards.slice(0, 4), ...cards.slice(5)];
    // ribbonCard = cards[5];
    tileCards = [...cards.slice(0, cards.length)];
  } else {
    ribbonCard = undefined;
    tileCards = cards;
  }

  return {
    key,
    heading: section?.title,
    ribbon: ribbonCard
      ? {
          text: ribbonCard?.title,
          metrics: ribbonCard?.metrics?.map((m: any) => ({
            label: m?.label,
            value: Number(m?.value),
          })),
          color: ribbonCard?.color,
        }
      : undefined,
    tiles: tileCards?.map((c: any) => ({
      title: c?.title,
      metrics: c?.metrics?.map((m: any) => ({
        label: m?.label,
        value: Number(m?.value),
      })),
      color: c?.color,
    })),
  };
};

const timeOptions: Array<SegmentedLabeledOption> = [
  { label: "Today", value: "today" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
];

// const schemeTabs: TabsProps["items"] = [
//   { key: "PMJAY", label: "PMJAY" },
//   { key: "CGHS", label: "CGHS" },
//   { key: "ECHS", label: "ECHS" },
//   { key: "CAPF", label: "CAPF" },
//   { key: "TPA", label: "TPA" },
// ];

const SectionHeader = ({
  text,
  icon,
}: {
  text: string;
  icon?: React.ReactNode;
}) => {
  const defaultIcon =
    !icon && text?.toLowerCase().includes("post") ? (
      <DatabaseOutlined style={{ color: "#1d4ed8" }} />
    ) : !icon && text?.toLowerCase().includes("pre") ? (
      <InboxOutlined style={{ color: "#1d4ed8" }} />
    ) : (
      icon
    );
  return (
    <Card
      size="small"
      styles={{ body: { padding: "12px 22px" } }}
      bordered={false}
      style={{
        background: "linear-gradient(180deg, #0f172a 0%, #1f2937 100%)",
        color: "#ffffff",
        width: "100%",
        borderRadius: 12,
        boxShadow: "0 10px 24px rgba(0,0,0,0.25)",
        border: "1px solid #0b1220",
        borderLeft: "6px solid #3b82f6",
      }}
    >
      <Flex align="center" justify="center" gap={8}>
        {defaultIcon}
        <Title
          level={5}
          style={{ margin: 0, color: "#ffffff", letterSpacing: 0.2 }}
        >
          {text}
        </Title>
      </Flex>
    </Card>
  );
};

const RibbonBar = ({
  text,
  metrics,
  color = "#f4e5e7",
}: {
  text: string;
  metrics?: StageMetric[];
  color?: string;
}) => (
  <Card
    bordered={false}
    size="small"
    styles={{
      body: {
        padding: "14px 24px",
        background: makeSoftBg(getAccentFor(text, color)),
        borderRadius: 14,
      },
    }}
    style={{
      borderRadius: 14,
      background: makeSoftBg(getAccentFor(text, color)),
      width: "75%",
      boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
      border: "1px solid #e5e7eb",
      borderLeft: `6px solid ${getAccentFor(text, color)}`,
    }}
  >
    <Flex justify="space-between" align="center" wrap="wrap" gap={10}>
      <Text strong style={{ fontSize: 15 }}>
        {text}
      </Text>
      {metrics && metrics.length > 0 && (
        <Space size={20} wrap>
          {metrics?.map((m) => (
            <Text key={m.label}>
              {m.label}:{" "}
              <Text strong style={{ fontSize: 15 }}>
                {m.value}
              </Text>
            </Text>
          ))}
        </Space>
      )}
    </Flex>
  </Card>
);

const OperationalDashboard: React.FC = () => {
  const TileCard = ({
    title,
    metrics,
    color,
    post,
  }: {
    title: string;
    metrics: StageMetric[];
    color: string;
    post: boolean;
  }) => {
    const accent = useMemo(() => getAccentFor(title, color), [title, color]);
    const softBg = useMemo(() => makeSoftBg(accent, 0.08), [accent]);

    const metricRows = [] as StageMetric[][];
    for (let i = 0; i < metrics.length; i += 2) {
      metricRows.push(metrics.slice(i, i + 2));
    }

    const cardHeight = metricRows.length * 30 + 60;

    const formatNumber = (value: number): string => {
      if (value === null || value === undefined || Number.isNaN(value))
        return "-";
      return new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
      }).format(value);
    };

    return (
      <Card
        bordered
        hoverable
        style={{
          background: softBg,
          borderRadius: 14,
          padding: 16,
          // minHeight: cardHeight,
          height: 140,
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxShadow: "0 4px 10px rgba(0,0,0,0.06)",
          border: `1px solid ${accent}33`,
          borderLeft: `4px solid ${accent}`,
          transition:
            "box-shadow 0.2s ease, transform 0.1s ease, border-color 0.2s ease",
        }}
        bodyStyle={{ padding: 0, height: "100%" }}
        className="tileCard"
      >
        <Title
          level={4}
          style={{ marginTop: 0, marginBottom: 10, color: "#0f172a" }}
        >
          {title}
        </Title>
        {/* <Space direction="vertical" size={6}>
        {metrics.map((m) => (
          <Text key={m.label} style={{ fontSize: 14 }}>
            {m.label}{" "}
            <Text strong style={{ fontSize: 16 }}>
              {m.value}
            </Text>
          </Text>
        ))}
      </Space> */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {metricRows?.map((row, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent:
                  row.length === 2 ? "space-between" : "flex-start",
                marginBottom: 8,
              }}
            >
              {row?.map((m, i) => (
                <span
                  key={m.label}
                  style={{
                    fontSize: 16,
                    minWidth: 0,
                    color: "#334155",
                    cursor: "pointer",
                  }}
                  // onClick={() => handleMetricClick(title, m.label)}
                >
                  {/* {m?.label} */}
                  <Link
                    style={{
                      fontSize: 16,
                      color: "#1677ff", // AntD primary color (looks professional)
                      cursor: "pointer",
                      marginRight: 4,
                    }}
                    onClick={
                      !post
                        ? () => handleMetricClick(title, m.label)
                        : undefined
                    }
                    className={!post ? "custom-link" : ""}
                  >
                    {m?.label === "Denied" ? "DENIAL/DROP" : m?.label}
                  </Link>
                  {": "}
                  <Text strong style={{ fontSize: 18, color: "#0b1324" }}>
                    {m?.value}
                  </Text>
                </span>
              ))}
              {row?.length === 1 && <span style={{ flex: 1 }} />}{" "}
              {/* Fill space if only one item */}
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const dispatch = useDispatch<AppDispatch>();
  const { selectedHospital } = useSelector(
    (state: RootState) => state.hospital
  );
  const { hospital } = useSelector((state: RootState) => state);
  const { schemes } = useSelector((state: RootState) => state.patients);

  const hospitalId = selectedHospital?.value;
  const allowedSchemeNames = [
    "CGHS NEW",
    "CAPF NEW",
    "CGHS",
    "CAPF",
    "ECHS",
    "Ayushman Bharat",
  ];
  const tpaScheme = {
    id: -1,
    schemeName: "TPA",
  };
  const primarySchemes = [
    ...Object.values(schemes)?.filter((scheme: any) =>
      allowedSchemeNames.includes(scheme.schemeName)
    ),
    tpaScheme,
  ];

  const schemeTabs = primarySchemes
    ?.filter((scheme) => scheme && scheme?.id !== undefined)
    ?.map((scheme) => ({
      label: getSchemeDisplayName(scheme) || scheme?.schemeName,
      key: String(scheme?.id),
    }));

  const [filters, setFilters] = useState<Filters>({
    submissionPart: "pre",
    timeRange: "today",
    timeRangePost: "today",
    scheme: "-1",
    schemePost: "-1",
    patientType: "IPD",
    patientTypePost: "IPD",
    metricMode: "count",
    metricModePost: "count",
    startDate: dayjs().format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    startDatePost: dayjs().format("YYYY-MM-DD"),
    endDatePost: dayjs().format("YYYY-MM-DD"),
  });

  const [startDate, setStartDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState<string>(dayjs().format("YYYY-MM-DD"));

  const [loading, setLoading] = useState(false);
  const [preData, setPreData] = useState<StageBlock | null>(null);
  const [postData, setPostData] = useState<StageBlock | null>(null);
  // const [lastRefreshed, setLastRefreshed] = useState<string | null>(null);
  const [refreshTime, setRefreshTime] = useState<string | null>(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [patientList, setPatientList] = useState<any>([]);
  const [modalStageName, setModalStageName] = useState("");
  const [modalStateName, setModalStateName] = useState("");
  // Pagination states for modal table
  const [modalPage, setModalPage] = useState(1);
  const [modalPageSize, setModalPageSize] = useState(20);
  const [modalTotal, setModalTotal] = useState(0);

  const handleDownloadExcel = async (stage: any, state: any) => {
    try {
      if (stage && state) {
        const data = {
          hospitalId: hospitalId,
          schemeId: Number(filters?.scheme),
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          admissionType: filters?.patientType === "IPD" ? "IP" : "OP",
          stage,
          state: state?.toUpperCase(),
        };
        setIsDownloadingExcel(true);
        await downloadExcelOperationalDashboard(data);
        setIsDownloadingExcel(false);
        successMessage("Excel Downloaded Successfully");
      }
    } catch (err) {
      setIsDownloadingExcel(false);
      errorMessage("Please, Try again later");
    }
  };

  const fetchPatientsForStageState = async (stage: any, state: any) => {
    setModalLoading(true);
    try {
      const res = await fetchPatientsOPDashboard({
        // ...filters,
        // stage,
        // state,
        // hospitalId: hospitalId,
        // schemeId: Number(filters.scheme),
        hospitalId: hospitalId,
        schemeId: Number(filters?.scheme),
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        admissionType: filters?.patientType === "IPD" ? "IP" : "OP",
        stage,
        state: state?.toUpperCase(),
      });
      setPatientList(res?.data || []);
      setModalTotal(res?.totalElements || 0);
    } catch (err) {
      setPatientList([]);
      setModalTotal(0);
    } finally {
      setModalLoading(false);
    }
  };

  // Separate function for modal table pagination and data fetch
  const handleModalTablePageChange = async (page: number, pageSize: number) => {
    setModalPage(page);
    setModalPageSize(pageSize);
    setModalLoading(true);
    try {
      const res = await fetchPatientsOPDashboard({
        ...filters,
        stage: modalStageName,
        state: modalStateName,
        hospitalId: hospitalId,
        pageNumber: page,
        pageSize: pageSize,
      });
      setPatientList(res?.data || []);
      setModalTotal(res?.totalElements || 0);
    } catch (err) {
      setPatientList([]);
      setModalTotal(0);
    } finally {
      setModalLoading(false);
    }
  };

  const handleMetricClick = (stage: any, state: any) => {
    setModalStageName(stage);
    setModalStateName(state);
    setModalVisible(true);
    fetchPatientsForStageState(stage, state);
  };

  const isTPAPost = filters.scheme === "-1";

  useEffect(() => {
    dispatch(getAllSchemes());
  }, []);

  async function fetchDashboardData(filters: Filters): Promise<{
    pre: StageBlock | null;
    post: StageBlock | null;
    refreshTime?: any;
  }> {
    try {
      if (
        !hospitalId ||
        !filters?.startDate ||
        !filters?.endDate ||
        !filters?.scheme
      ) {
        return {
          pre: null,
          post: null,
        };
      }
      const res = await fetchOperationalDashboard({
        hospitalId: hospitalId,
        schemeId: Number(filters?.scheme),
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        criteria: filters?.metricMode,
        admissionType: filters?.patientType === "IPD" ? "IP" : "OP",
      });
      const apiResponse = res?.data;
      return {
        pre: transform("pre", apiResponse.preSubmission),
        post: transform("post", apiResponse.postSubmission),
        refreshTime: apiResponse.refreshTime,
      };
    } catch (error) {
      console.error("Error in fetching operational dashboard data:", error);
      return {
        pre: null,
        post: null,
      };
    }

    //   const apiResponse = {
    //     schemeName: "Operational Dashboard",
    //     preSubmission: {
    //       title: "Pre Submission",
    //       cards: [
    //         {
    //           title: "Internal Query",
    //           status: "Active",
    //           color: "#f4e5e7",
    //           metrics: [
    //             { label: "Count", value: "288", units: "count" },
    //             { label: "Open", value: "38", units: "count" },
    //           ],
    //         },
    //         {
    //           title: "Visit Created",
    //           status: "Active",
    //           color: "#efe9fb",
    //           metrics: [
    //             { label: "Count", value: "2000", units: "count" },
    //             { label: "Drop / Cash", value: "5", units: "count" },
    //           ],
    //         },
    //         {
    //           title: "Portal Registration",
    //           status: "Active",
    //           color: "#fbf7e9",
    //           metrics: [
    //             { label: "Count", value: "1978", units: "count" },
    //             { label: "Pending", value: "17", units: "count" },
    //           ],
    //         },
    //         {
    //           title: "Preauthorisation",
    //           status: "Active",
    //           color: "#e9f3fb",
    //           metrics: [
    //             { label: "Count", value: "1701", units: "count" },
    //             { label: "Pending", value: "277", units: "count" },
    //           ],
    //         },
    //         {
    //           title: "Submitted",
    //           status: "Active",
    //           color: "#e9f6ea",
    //           metrics: [
    //             { label: "Count", value: "1650", units: "count" },
    //             { label: "Pending", value: "51", units: "count" },
    //           ],
    //         },
    //       ],
    //     },
    //     postSubmission: {
    //       title: "Post Submission",
    //       cards: [
    //         {
    //           title: "QC Query",
    //           status: "Active",
    //           color: "#f4e5e7",
    //           metrics: [
    //             { label: "Count", value: "119", units: "count" },
    //             { label: "Open", value: "1", units: "count" },
    //           ],
    //         },
    //         {
    //           title: "Submitted",
    //           status: "Active",
    //           color: "#e9f6ea",
    //           metrics: [
    //             { label: "Count", value: "1650", units: "count" },
    //             { label: "Reject", value: "2", units: "count" },
    //           ],
    //         },
    //         // ... other post cards
    //       ],
    //     },
    //   };
  }

  const handleDateRangeChange = (dates: any, dateStrings: any) => {
    setStartDate(dateStrings[0]);
    setEndDate(dateStrings[1]);
    setFilters((f: any) => ({
      ...f,
      startDate: dayjs(dateStrings[0]).format("YYYY-MM-DD"),
      endDate: dayjs(dateStrings[1]).format("YYYY-MM-DD"),
      timeRange: null,
    }));
  };

  async function fetchDashboardDataPost(filters: Filters): Promise<{
    pre: StageBlock | null;
    post: StageBlock | null;
  }> {
    try {
      if (
        !hospitalId ||
        !filters?.startDatePost ||
        !filters?.endDatePost ||
        !filters?.schemePost
      ) {
        return {
          pre: null,
          post: null,
        };
      }
      const res = await fetchOperationalDashboard({
        hospitalId: hospitalId,
        schemeId: Number(filters?.schemePost),
        startDate: filters?.startDate,
        endDate: filters?.endDate,
        criteria: filters?.metricModePost,
        admissionType: filters?.patientTypePost === "IPD" ? "IP" : "OP",
      });
      const apiResponse = res?.data;
      return {
        pre: transform("pre", apiResponse.preSubmission),
        post: transform("post", apiResponse.postSubmission),
      };
    } catch (error) {
      console.error("Error in fetching operational dashboard data:", error);
      return {
        pre: null,
        post: null,
      };
    }
  }

  const runQuery = useCallback(async () => {
    setLoading(true);
    try {
      // if (filters?.submissionPart === "pre") {
      //   const data = await fetchDashboardData(filters);
      //   setPreData(data.pre);
      // } else {
      //   const data = await fetchDashboardDataPost(filters);
      //   setPostData(!isTPAPost ? data.post : null);
      // }
      const data = await fetchDashboardData(filters);
      setPreData(data.pre);
      setPostData(!isTPAPost ? data.post : null);
      // if (data.refreshTime) {
      //   setRefreshTime(data.refreshTime);
      // }
      setRefreshTime(data.refreshTime);
    } finally {
      setLoading(false);
    }
  }, [filters, isTPAPost, hospital?.selectedHospital?.value]);

  useEffect(() => {
    runQuery();
  }, [runQuery]);

  //   const handleTimeChange = (val: string | number) =>
  //     setFilters((f) => ({ ...f, timeRange: val as TimeRange }));

  const handleTimeChange = (val: string | number) => {
    let start = dayjs();
    let end = dayjs();
    if (val === "today") {
      start = end = dayjs();
    } else if (val === "weekly") {
      start = dayjs().subtract(7, "day");
      end = dayjs();
    } else if (val === "monthly") {
      start = dayjs().subtract(1, "month");
      end = dayjs();
    } else if (val === "quarterly") {
      start = dayjs().subtract(3, "month");
      end = dayjs();
    }
    setStartDate(start.format("YYYY-MM-DD"));
    setEndDate(end.format("YYYY-MM-DD"));
    setFilters((f) => ({
      ...f,
      submissionPart: "pre",
      startDate: start.format("YYYY-MM-DD"),
      endDate: end.format("YYYY-MM-DD"),
      timeRange: val as TimeRange,
    }));
  };

  const handleTimeChangePost = (val: string | number) => {
    let start = dayjs();
    let end = dayjs();
    if (val === "today") {
      start = end = dayjs();
    } else if (val === "weekly") {
      start = dayjs().subtract(7, "day");
      end = dayjs();
    } else if (val === "monthly") {
      start = dayjs().subtract(1, "month");
      end = dayjs();
    } else if (val === "quarterly") {
      start = dayjs().subtract(3, "month");
      end = dayjs();
    }
    setStartDate(start.format("YYYY-MM-DD"));
    setEndDate(end.format("YYYY-MM-DD"));
    setFilters((f) => ({
      ...f,
      submissionPartPost: "post",
      startDatePost: start.format("YYYY-MM-DD"),
      endDatePost: end.format("YYYY-MM-DD"),
      timeRangePost: val as TimeRange,
    }));
  };

  const handleSchemeChangePost: TabsProps["onChange"] = (key) =>
    setFilters((f) => ({ ...f, submissionPart: "post", schemePost: key }));

  const handlePatientTogglePost = (checked: boolean) =>
    setFilters((f) => ({
      ...f,
      submissionPart: "post",
      patientTypePost: checked ? "OPD" : "IPD",
    }));

  const handleMetricTogglePost = (checked: boolean) =>
    setFilters((f) => ({
      ...f,
      submissionPart: "post",
      metricModePost: checked ? "count" : "amount",
    }));

  const handleSchemeChange: TabsProps["onChange"] = (key) =>
    setFilters((f) => ({ ...f, submissionPart: "pre", scheme: key }));

  const handlePatientToggle = (checked: boolean) =>
    setFilters((f) => ({
      ...f,
      submissionPart: "pre",
      patientType: checked ? "OPD" : "IPD",
    }));

  const handleMetricToggle = (checked: boolean) =>
    setFilters((f) => ({
      ...f,
      submissionPart: "pre",
      metricMode: checked ? "count" : "amount",
    }));

  const handleHospitalChange = (hospitalId: number) => {
    if (hospital.selectedHospital?.value !== hospitalId) {
      dispatch(updateSelectedHospital(hospitalId));
      dispatch(resetAddIPDBillState());
      dispatch(resetHospitalToSchemeCodesMap());
      successMessage("HOSPITAL UPDATE");
    }
  };

  const handlePostRefresh = async () => {
    if (!hospitalId || hospitalId <= 0) {
      errorMessageModal("Please select another hospital");
      return;
    }
    try {
      setRefreshLoading(true);
      await refreshByHospitalId(hospitalId as number);
      successMessage(
        "Portal Status Refresh Process Initiated Successfully. Please check in Sometime."
      );
      runQuery();
    } catch (error) {
      console.error("Error in refreshing:", error);
    } finally {
      setRefreshLoading(false);
    }
  };

  const getSelectedSchemeLabel = () => {
    return schemeTabs?.find((scheme) => scheme.key === filters.scheme)?.label;
  };

  const FilterBar = (
    <Card
      size="small"
      bordered={false}
      styles={{ body: { padding: "10px 14px" } }}
      style={{
        borderRadius: 14,
        boxShadow: "0 8px 18px rgba(0,0,0,0.06)",
        background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
        border: "1px solid #e5e7eb",
      }}
    >
      <Row gutter={[12, 12]} align="middle" justify="center">
        <Col xs={24} md={24}>
          {/* <Space size={10} wrap> */}
          {/* <Text>Time</Text> */}
          {/* <Segmented
              size="large"
              options={timeOptions}
              value={filters.timeRange}
              onChange={handleTimeChange}
            /> */}
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  itemSelectedBg: "#1d4ed8",
                  itemSelectedColor: "#1d4ed8",
                  trackBg: "#f3f4f6",
                  colorBgLayout: "#ffffff",
                  controlHeightLG: 40,
                },
              },
            }}
          >
            <Segmented
              block
              size="large"
              options={timeOptions}
              value={filters?.timeRange}
              onChange={handleTimeChange}
              style={{ width: "100%" }}
              className="timeSegmented"
            />
          </ConfigProvider>
          {/* </Space> */}
        </Col>
        <Col xs={24} md={8}>
          {/* <Tabs
            items={schemeTabs}
            activeKey={filters.scheme}
            onChange={handleSchemeChange}
            tabBarStyle={{
              marginBottom: 0,
            }}
          /> */}
        </Col>
      </Row>
      <Row gutter={[12, 12]} justify="center">
        <Col span={25}>
          <ConfigProvider
            theme={{
              components: {
                Tabs: {
                  cardBg: "#f3f4f6",
                  itemHoverColor: "#0f172a",
                  itemColor: "#334155",
                  itemActiveColor: "#111827",
                  colorPrimary: "#1d4ed8",
                },
              },
            }}
          >
            <div className="operationalTabsContainer">
              <Tabs
                items={schemeTabs}
                activeKey={filters.scheme}
                type="card"
                onChange={handleSchemeChange}
                tabBarStyle={{
                  marginBottom: 0,
                  display: "flex",
                  justifyContent: "center",
                }}
                className="operationalDashboardTabs"
              />
            </div>
          </ConfigProvider>
        </Col>
      </Row>
      <Row gutter={[12, 12]} justify="center" style={{ marginTop: 10 }}>
        <Col xs={24} md={24}>
          <Flex align="center" justify="center" gap={100} wrap="wrap">
            <Space>
              <Select
                showSearch
                placeholder="Select a Hospital"
                optionFilterProp="label"
                style={{ width: 150 }}
                value={selectedHospital ? selectedHospital.value : undefined}
                options={
                  hospital.availableHospitals &&
                  hospital.availableHospitals?.map((hospital) => ({
                    label: hospital.label,
                    value: hospital.value,
                  }))
                }
                onChange={handleHospitalChange}
              />
            </Space>
            <Space>
              <Text>IPD</Text>
              <ConfigProvider
                theme={{
                  components: {
                    Switch: {
                      colorPrimary: "#1d4ed8",
                      handleBg: "#ffffff",
                      colorPrimaryHover: "#1e40af",
                    },
                  },
                }}
              >
                <Switch
                  checkedChildren="OPD"
                  unCheckedChildren="IPD"
                  checked={filters.patientType === "OPD"}
                  onChange={handlePatientToggle}
                />
              </ConfigProvider>
              <Text>OPD</Text>
            </Space>
            <Space>
              <Text>Amount</Text>
              <ConfigProvider
                theme={{
                  components: {
                    Switch: {
                      colorPrimary: "#1d4ed8",
                      handleBg: "#ffffff",
                      colorPrimaryHover: "#1e40af",
                    },
                  },
                }}
              >
                <Switch
                  checkedChildren="Count"
                  unCheckedChildren="Amount"
                  checked={filters.metricMode === "count"}
                  onChange={handleMetricToggle}
                />
              </ConfigProvider>
              <Text>Count</Text>
            </Space>
            <Space>
              <RangePicker
                value={
                  filters.startDate && filters.endDate
                    ? [dayjs(filters.startDate), dayjs(filters.endDate)]
                    : undefined
                }
                format="YYYY-MM-DD"
                onChange={handleDateRangeChange}
                style={{ width: "100%" }}
              />
            </Space>
          </Flex>
        </Col>
        {/* <Col xs={24} md={12} style={{ marginTop: 10 }}>
        
      </Col> */}
      </Row>
    </Card>
  );

  const FilterBarPost = (
    <Card
      size="small"
      bordered={false}
      styles={{ body: { padding: "10px 14px" } }}
      style={{
        borderRadius: 14,
        boxShadow: "0 8px 18px rgba(0,0,0,0.06)",
        background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
        border: "1px solid #e5e7eb",
      }}
    >
      <Row gutter={[12, 12]} align="middle" justify="center">
        <Col xs={24} md={24}>
          {/* <Space size={10} wrap> */}
          {/* <Text>Time</Text> */}
          {/* <Segmented
              size="large"
              options={timeOptions}
              value={filters.timeRange}
              onChange={handleTimeChange}
            /> */}
          <ConfigProvider
            theme={{
              components: {
                Segmented: {
                  itemSelectedBg: "#1d4ed8",
                  itemSelectedColor: "#1d4ed8",
                  trackBg: "#f3f4f6",
                  colorBgLayout: "#ffffff",
                  controlHeightLG: 40,
                  // itemColor: "#fff"
                },
              },
            }}
          >
            <Segmented
              block
              size="large"
              options={timeOptions}
              value={filters?.timeRangePost}
              onChange={handleTimeChangePost}
              style={{ width: "100%" }}
              className="timeSegmented"
            />
          </ConfigProvider>
          {/* </Space> */}
        </Col>
        <Col xs={24} md={8}>
          {/* <Tabs
            items={schemeTabs}
            activeKey={filters.scheme}
            onChange={handleSchemeChange}
            tabBarStyle={{
              marginBottom: 0,
            }}
          /> */}
        </Col>
      </Row>
      <Row gutter={[12, 12]} justify="center">
        <Col span={24}>
          <ConfigProvider
            theme={{
              components: {
                Tabs: {
                  cardBg: "#f3f4f6",
                  itemHoverColor: "#0f172a",
                  itemColor: "#334155",
                  itemActiveColor: "#111827",
                  colorPrimary: "#1d4ed8",
                },
              },
            }}
          >
            <div className="operationalTabsContainer">
              <Tabs
                items={schemeTabs}
                activeKey={filters.schemePost}
                type="card"
                onChange={handleSchemeChangePost}
                tabBarStyle={{
                  marginBottom: 0,
                  display: "flex",
                  justifyContent: "center",
                }}
                className="operationalDashboardTabs"
              />
            </div>
          </ConfigProvider>
        </Col>
      </Row>
      <Row gutter={[12, 12]} justify="center" style={{ marginTop: 10 }}>
        <Col xs={24} md={24}>
          <Flex align="center" justify="center" gap={100} wrap="wrap">
            <Space>
              <Text>IPD</Text>
              <ConfigProvider
                theme={{
                  components: {
                    Switch: {
                      colorPrimary: "#1d4ed8",
                      handleBg: "#ffffff",
                      colorPrimaryHover: "#1e40af",
                    },
                  },
                }}
              >
                <Switch
                  checkedChildren="OPD"
                  unCheckedChildren="IPD"
                  checked={filters.patientTypePost === "OPD"}
                  onChange={handlePatientTogglePost}
                />
              </ConfigProvider>
              <Text>OPD</Text>
            </Space>
            <Space>
              <Text>Amount</Text>
              <ConfigProvider
                theme={{
                  components: {
                    Switch: {
                      colorPrimary: "#1d4ed8",
                      handleBg: "#ffffff",
                      colorPrimaryHover: "#1e40af",
                    },
                  },
                }}
              >
                <Switch
                  checkedChildren="Count"
                  unCheckedChildren="Amount"
                  checked={filters.metricModePost === "count"}
                  onChange={handleMetricTogglePost}
                />
              </ConfigProvider>
              <Text>Count</Text>
            </Space>
          </Flex>
        </Col>
      </Row>
    </Card>
  );

  const TileRow = ({
    data,
    post = false,
  }: {
    data: StageBlock | null;
    post: boolean;
  }) => {
    if (!data) return null;

    return (
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {loading ? (
          <Row gutter={[12, 12]}>
            {[1, 2, 3, 4]?.map((k) => (
              <Col key={k} xs={24} sm={12} md={12} lg={8} xl={8}>
                <Card
                  bordered={false}
                  style={{
                    borderRadius: 20,
                    boxShadow: "0 10px 24px rgba(0,0,0,0.08)",
                  }}
                >
                  <Skeleton
                    active
                    paragraph={{ rows: 3 }}
                    title={{ width: "60%" }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        ) : (
          data.tiles && (
            <Row gutter={[12, 12]}>
              {data.tiles.map((t, idx) => (
                <Col key={t.title} xs={24} sm={12} md={12} lg={8} xl={8}>
                  <TileCard
                    title={t.title}
                    metrics={t.metrics}
                    color={t.color}
                    post={post}
                  />
                </Col>
              ))}
            </Row>
          )
        )}

        {loading ? (
          // <Flex justify="center">
          //   <Card
          //     bordered={false}
          //     style={{
          //       width: "80%",
          //       borderRadius: 16,
          //       boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
          //     }}
          //   >
          //     <Skeleton active paragraph={{ rows: 1 }} title={false} />
          //   </Card>
          // </Flex>
          <></>
        ) : (
          data.ribbon && (
            <Flex justify="center">
              <RibbonBar
                text={data.ribbon.text}
                metrics={data.ribbon.metrics}
                color={data.ribbon.color}
              />
            </Flex>
          )
        )}
      </Space>
    );
  };

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
  });

  useEffect(() => {
    if (!modalVisible) {
      setPagination({
        current: 1,
        pageSize: 100,
      });
    }
  }, [modalVisible]);

  return (
    <>
      <ConfigProvider
        theme={{
          token: {
            borderRadiusLG: 16,
            colorBorder: "#e5e7eb",
            colorBgContainer: "#ffffff",
          },
        }}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {FilterBar}
          <Flex style={{ width: "25%" }}>
            <SectionHeader text="Pre Submission" />
          </Flex>

          {/* <Divider style={{ margin: "8px 0" }} /> */}
          <TileRow data={preData} post={false} />

          {!isTPAPost && (
            <>
              <Divider style={{ margin: "6px 10px" }} />
              <Flex
                style={{ width: "100%", marginTop: "1rem" }}
                justify="space-between"
                align="center"
              >
                <div style={{ width: "25%" }}>
                  <SectionHeader text="Post Submission" />
                </div>
                {getSelectedSchemeLabel() === "CGHS" && refreshTime && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      marginLeft: "16px",
                    }}
                  >
                    {
                      // dayjs().diff(
                      //   dayjs(refreshTime, "DD-MM-YYYY HH:mm:ss"),
                      //   "minute"
                      // ) > 120 &&
                      <Button
                        type="primary"
                        disabled={
                          dayjs().diff(
                            dayjs(refreshTime, "DD-MM-YYYY HH:mm:ss"),
                            "minute"
                          ) < 120
                        }
                        loading={refreshLoading}
                        onClick={handlePostRefresh}
                        style={{
                          color: "#f3f4f6",
                          background:
                            dayjs().diff(
                              dayjs(refreshTime, "DD-MM-YYYY HH:mm:ss"),
                              "minute"
                            ) < 120
                              ? "#93c5fd" // lighter blue when disabled
                              : "#1d4ed8",
                          border: "none",
                          padding: "8px 14px",
                          borderRadius: 8,
                          boxShadow: "0 6px 12px rgba(29,78,216,0.25)",
                          cursor:
                            dayjs().diff(
                              dayjs(refreshTime, "DD-MM-YYYY HH:mm:ss"),
                              "minute"
                            ) < 120
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        Refresh
                      </Button>
                    }
                    {refreshTime && (
                      <Text
                        style={{
                          marginTop: 0,
                          display: "block",
                          color: "black",
                          fontSize: 14,
                          fontWeight: 700,
                        }}
                      >
                        Last Refreshed :{" "}
                        <span style={{ fontWeight: 700 }}>{refreshTime}</span>
                      </Text>
                    )}
                  </div>
                )}
              </Flex>
              {/* <Divider style={{ margin: "8px 0" }} /> */}
              {/* {FilterBarPost} */}
              <TileRow data={postData} post={true} />
            </>
          )}
        </Space>
      </ConfigProvider>
      <Modal
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width="100vw"
        style={{
          top: 10,
          left: 4,
          padding: 0,
          maxWidth: "100vw",
          height: "100vh",
        }}
        wrapClassName="operational-table-modal"
        bodyStyle={{
          height: "calc(130vh - 55px)",
          overflow: "auto",
          padding: 0,
        }}
        title={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div>
              <span style={{ fontWeight: 600 }}>{modalStageName}</span>
              {" - "}
              <span style={{ color: "#1d4ed8" }}>
                {modalStateName === "Denied" ? "DENIAL/DROP" : modalStateName}{" "}
                {`(${patientList?.length})`}
              </span>
            </div>
            <Button
              type="primary"
              style={{
                background: "rgb(0, 76, 143)",
                borderColor: "rgb(0, 84, 157)",
                width: 150,
                fontSize: "0.9rem",
                borderRadius: 6,
                marginRight: 30,
                marginBottom: 10,
              }}
              loading={isDownloadingExcel}
              icon={<FileExcelOutlined />}
              onClick={async () => {
                await handleDownloadExcel(modalStageName, modalStateName);
              }}
            >
              Export Excel
            </Button>
          </div>
        }
      >
        <div className="operational-table-modal">
          <Table
            loading={modalLoading}
            dataSource={patientList}
            className="operational-dashboard-table"
            columns={[
              {
                title: "S.No.",
                dataIndex: "sNo",
                key: "sNo",
                render: (_: any, __: any, index: number) =>
                  (pagination.current - 1) * pagination.pageSize + index + 1,
                width: 80,
              },
              {
                title: "Credit Number",
                dataIndex: "creditNumber",
                key: "creditNumber",
                render: (text: string) =>
                  text && text.length > 10 ? (
                    <Tooltip title={text}>
                      <span>{text.substring(0, 10) + "..."}</span>
                    </Tooltip>
                  ) : (
                    <span>{text}</span>
                  ),
              },
              {
                title: "Patient Name",
                dataIndex: "patientName",
                key: "patientName",
                render: (text: string) =>
                  text && text.length > 10 ? (
                    <Tooltip title={text}>
                      <span style={{ fontWeight: 500 }}>
                        {text.substring(0, 10) + "..."}
                      </span>
                    </Tooltip>
                  ) : (
                    <span style={{ fontWeight: 500 }}>{text}</span>
                  ),
              },
              {
                title: "Scheme Name",
                dataIndex: "schemeName",
                key: "schemeName",
                render: (text: string) =>
                  text && text.length > 10 ? (
                    <Tooltip title={text}>
                      <span style={{}}>{text.substring(0, 10) + "..."}</span>
                    </Tooltip>
                  ) : (
                    <span>{text}</span>
                  ),
              },
              // {
              //   title: "Hospital Name",
              //   dataIndex: "hospitalName",
              //   key: "hospitalName",
              //   render: (text: string) =>
              //     text && text.length > 10 ? (
              //       <Tooltip title={text}>
              //         <span style={{ color: "#334155" }}>
              //           {text.substring(0, 10) + "..."}
              //         </span>
              //       </Tooltip>
              //     ) : (
              //       <span>{text}</span>
              //     ),
              // },
              // {
              //   title: "Case Type",
              //   dataIndex: "caseType",
              //   key: "caseType",
              //   render: (text: string) =>
              //     text && text.length > 10 ? (
              //       <Tooltip title={text}>
              //         <span>{text.substring(0, 10) + "..."}</span>
              //       </Tooltip>
              //     ) : (
              //       <span>{text}</span>
              //     ),
              // },
              // {
              //   title: "Claim ID",
              //   dataIndex: "claimId",
              //   key: "claimId",
              //   render: (text: string) =>
              //     text && text.length > 10 ? (
              //       <Tooltip title={text}>
              //         <span>{text.substring(0, 10) + "..."}</span>
              //       </Tooltip>
              //     ) : (
              //       <span>{text}</span>
              //     ),
              // },
              {
                title: "UHID No",
                dataIndex: "uhidNo",
                key: "uhidNo",
                render: (text: string) =>
                  text && text.length > 10 ? (
                    <Tooltip title={text}>
                      <span>{text.substring(0, 10) + "..."}</span>
                    </Tooltip>
                  ) : (
                    <span>{text}</span>
                  ),
              },
              {
                title: "OP Number",
                dataIndex: "opNumber",
                key: "opNumber",
                render: (text: string) =>
                  text && text.length > 10 ? (
                    <Tooltip title={text}>
                      <span>{text.substring(0, 10) + "..."}</span>
                    </Tooltip>
                  ) : (
                    <span>{text}</span>
                  ),
              },
              {
                title: "Visit Type",
                dataIndex: "visitType",
                key: "visitType",
                render: (text: string) =>
                  text && text.length > 10 ? (
                    <Tooltip title={text}>
                      <span>{text.substring(0, 10) + "..."}</span>
                    </Tooltip>
                  ) : (
                    <span>{text}</span>
                  ),
              },
              {
                title: "DOA",
                dataIndex: "dateOfAdmission",
                key: "dateOfAdmission",
                render: (text: string) =>
                  text && text.length > 10 ? (
                    <Tooltip title={text}>
                      <span>{text.substring(0, 10) + "..."}</span>
                    </Tooltip>
                  ) : (
                    <span>{text}</span>
                  ),
              },
              // {
              //   title: "Status",
              //   dataIndex: "status",
              //   key: "status",
              //   render: (text: string) =>
              //     text && text.length > 10 ? (
              //       <Tooltip title={text}>
              //         <span>{text.substring(0, 10) + "..."}</span>
              //       </Tooltip>
              //     ) : (
              //       <span>{text}</span>
              //     ),
              // },
              // {
              //   title: "Portal Status",
              //   dataIndex: "portalStatus",
              //   key: "portalStatus",
              //   render: (text: string) =>
              //     text && text.length > 10 ? (
              //       <Tooltip title={text}>
              //         <span>{text.substring(0, 10) + "..."}</span>
              //       </Tooltip>
              //     ) : (
              //       <span>{text}</span>
              //     ),
              // },
              // {
              //   title: "Bill Number",
              //   dataIndex: "billNumber",
              //   key: "billNumber",
              //   render: (text: string) =>
              //     text && text.length > 10 ? (
              //       <Tooltip title={text}>
              //         <span>{text.substring(0, 10) + "..."}</span>
              //       </Tooltip>
              //     ) : (
              //       <span>{text}</span>
              //     ),
              // },
              // {
              //   title: "Bill Amount",
              //   dataIndex: "billAmount",
              //   key: "billAmount",
              //   render: (text: string) =>
              //     text && text.length > 10 ? (
              //       <Tooltip title={text}>
              //         <span>{text.substring(0, 10) + "..."}</span>
              //       </Tooltip>
              //     ) : (
              //       <span>{text}</span>
              //     ),
              // },
            ]}
            rowKey="id"
            // pagination={{
            //   current: modalPage,
            //   pageSize: modalPageSize,
            //   total: modalTotal,
            //   showSizeChanger: true,
            //   pageSizeOptions: ["10", "20", "50", "100"],
            //   onChange: handleModalTablePageChange,
            // }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: patientList.length, // or API total if paginated server-side
              showSizeChanger: true,
              pageSizeOptions: ["100"],
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize });
              },
            }}
            // pagination={{
            //   pageSize: 50,
            //   pageSizeOptions: ["50"],
            // }}
            rowClassName={(record, index) =>
              index % 2 === 0 ? "ant-table-row-light" : ""
            }
            scroll={{
              x: 150,
              y: 600,
            }}
            style={{
              borderRadius: 16,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              background: "#fff",
            }}
          />
        </div>
      </Modal>
    </>
  );
};

export default OperationalDashboard;
